// GA4 event helper. Every event added since Nov 2026 goes through track();
// older calls still use window.gtag directly and keep their names (reports
// depend on them). The full event list lives in ANALYTICS.md at the repo root.
//
// Rules this file enforces so a call site can't break them by accident:
//  - never throws, and no-ops when gtag isn't loaded (dev, ad blockers)
//  - drops undefined / null / '' params
//  - GA4 limits: event and param names <= 40 chars, <= 25 params, values <= 100 chars
//
// What must never be sent (callers' job, the helpers below make it easy): email,
// name, phone, street address, free-text notes, pasted quotes, exact ZIP. State
// (2-letter) and coarse buckets are fine; multiple-choice answers are fine.

import { useEffect, useRef } from 'react';

const MAX_PARAMS = 25;
const MAX_NAME = 40;
const MAX_VALUE = 100;

export function track(name, params) {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const clean = {};
    let count = 0;
    Object.keys(params || {}).forEach((key) => {
      if (count >= MAX_PARAMS) return;
      let v = params[key];
      if (v === undefined || v === null || v === '' || (typeof v === 'number' && !Number.isFinite(v))) return;
      if (typeof v !== 'number' && typeof v !== 'boolean') v = String(v).slice(0, MAX_VALUE);
      clean[String(key).slice(0, MAX_NAME)] = v;
      count += 1;
    });
    window.gtag('event', String(name).slice(0, MAX_NAME), clean);
  } catch (_) {
    // analytics must never break the page
  }
}

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE_RE = /\+?\d[\d\s().-]{5,}\d/g; // 7+ digits with separators
const DIGIT_RUN_RE = /\d{5,}/g; // ZIPs, account numbers

// Strip anything that looks like an email, phone number or ZIP.
export function scrub(text) {
  return String(text || '').replace(EMAIL_RE, '').replace(PHONE_RE, '').replace(DIGIT_RUN_RE, '').replace(/\s+/g, ' ').trim();
}

// A URL path with any segment that holds an email, phone number or long
// digit run replaced by ':redacted' (for 404 reports).
export function scrubPath(path) {
  return String(path || '').split('/').map((seg) => {
    let decoded = seg;
    try { decoded = decodeURIComponent(seg); } catch (_) { /* keep raw */ }
    return scrub(decoded) === decoded.trim() ? seg : ':redacted';
  }).join('/');
}

// Search box params: the term itself only when it's a short query (<= 50
// chars) with emails / phone numbers / ZIPs stripped; otherwise just the length.
export function searchParams(raw) {
  const term = String(raw || '').trim();
  const out = { search_length: term.length };
  if (term.length > 0 && term.length <= 50) {
    const safe = scrub(term).toLowerCase();
    if (safe) out.search_term = safe;
  }
  return out;
}

// Coarse bucket for a count or amount: bucket(3, [0, 1, 2, 4]) -> '2-3'.
// edges are ascending lower bounds; the last bucket is open-ended ('4+').
export function bucket(n, edges) {
  const v = Number(n);
  if (!Number.isFinite(v)) return undefined;
  for (let i = edges.length - 1; i >= 0; i -= 1) {
    if (v >= edges[i]) {
      if (i === edges.length - 1) return `${edges[i]}+`;
      const hi = edges[i + 1] - 1;
      return hi === edges[i] ? String(edges[i]) : `${edges[i]}-${hi}`;
    }
  }
  return `<${edges[0]}`;
}

// Fire `search` once the visitor stops typing (1.2s), at most once per
// distinct term. `results` is the match count for that term, if known.
export function useSearchTracking(term, context, results) {
  const last = useRef('');
  const latest = useRef(results);
  latest.current = results;
  useEffect(() => {
    const t = String(term || '').trim();
    if (t.length < 2 || t === last.current) return undefined;
    const id = setTimeout(() => {
      last.current = t;
      track('search', { ...searchParams(t), search_context: context, results: latest.current });
    }, 1200);
    return () => clearTimeout(id);
  }, [term, context]);
}

// Calculators: inputChanged(field) on every user edit. Fires
// calculator_input once per field per page view, and calculator_result
// (with whatever getResult() returns) 2s after the last edit.
export function useCalculatorTracking(calculator, getResult) {
  const seen = useRef(new Set());
  const timer = useRef(null);
  const resultRef = useRef(getResult);
  resultRef.current = getResult;
  useEffect(() => () => clearTimeout(timer.current), []);
  return (field) => {
    if (!seen.current.has(field)) {
      seen.current.add(field);
      track('calculator_input', { calculator, field });
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      let extra = {};
      try { extra = resultRef.current ? resultRef.current() : {}; } catch (_) { extra = {}; }
      track('calculator_result', { calculator, fields_changed: Array.from(seen.current).join('|'), ...extra });
    }, 2000);
  };
}

// ---------------------------------------------------------------------------
// Site-wide click tracking (installed once in App.jsx). One delegated listener
// covers every link on every page, so new pages get it for free:
//   outbound_click      {link_domain, link_url, link_text, context}  any http(s) link off-site
//   internal_link_click {link_path, link_text, context}              any on-site link
//   download_click      {file_name, file_extension, context}         a[download] or a .pdf/.png/.svg link
//   contact_click       {method, context}                            mailto: / tel:
//   cta_click           {cta, context, link_path}                    any element with data-cta="name"
// context = nearest data-track-context="..." ancestor, else nav / nav_drawer /
// footer / prefooter / page. URLs are sent without query string or hash.
// An on-site link that carries data-cta sends cta_click instead of
// internal_link_click (not both).

const linkText = (el) => scrub((el.getAttribute('aria-label') || el.textContent || '').replace(/\s+/g, ' ')).slice(0, MAX_VALUE);

export function contextFor(el) {
  const tagged = el.closest('[data-track-context]');
  if (tagged) return tagged.getAttribute('data-track-context');
  if (el.closest('.fi-drawer')) return 'nav_drawer';
  if (el.closest('nav.fi-nav, .fi-strip, .fi-mast')) return 'nav';
  if (el.closest('.fi-prefoot')) return 'prefooter';
  if (el.closest('footer')) return 'footer';
  return 'page';
}

const DOWNLOAD_EXT = /\.(pdf|png|svg|jpe?g|zip|csv)$/i;

function onDocumentClick(e) {
  try {
    if (e.type === 'auxclick' && e.button !== 1) return; // middle-click only
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    // Both are ancestors of the click target, so when both exist one holds the other.
    const ctaEl = target.closest('[data-cta]');
    const a = target.closest('a[href]');
    if (ctaEl) {
      const to = ctaEl.getAttribute('data-cta-to') || (a && a.getAttribute('href')) || '';
      track('cta_click', { cta: ctaEl.getAttribute('data-cta'), context: contextFor(ctaEl), link_path: to.split(/[?#]/)[0] });
    }
    if (!a) return;

    const href = a.getAttribute('href') || '';
    const context = contextFor(a);
    if (/^mailto:/i.test(href)) { track('contact_click', { method: 'email', context }); return; }
    if (/^tel:/i.test(href)) { track('contact_click', { method: 'phone', context }); return; }

    let url;
    try { url = new URL(a.href, window.location.href); } catch (_) { return; }
    if (!/^https?:$/.test(url.protocol)) return;

    if (url.host !== window.location.host) {
      track('outbound_click', {
        link_domain: url.hostname.replace(/^www\./, ''),
        link_url: `${url.origin}${url.pathname}`,
        link_text: linkText(a),
        context,
      });
      return;
    }
    const file = url.pathname.split('/').pop() || '';
    if (a.hasAttribute('download') || DOWNLOAD_EXT.test(file)) {
      track('download_click', { file_name: file, file_extension: (file.split('.').pop() || '').toLowerCase(), context });
      return;
    }
    if (ctaEl) return; // already sent as cta_click
    const samePageHash = url.pathname === window.location.pathname && url.hash;
    track('internal_link_click', {
      link_path: samePageHash ? `${url.pathname}${url.hash}` : url.pathname,
      link_text: linkText(a),
      context,
    });
  } catch (_) {
    // never break a click
  }
}

export function installClickTracking() {
  if (typeof document === 'undefined') return () => {};
  document.addEventListener('click', onDocumentClick, true);
  document.addEventListener('auxclick', onDocumentClick, true);
  return () => {
    document.removeEventListener('click', onDocumentClick, true);
    document.removeEventListener('auxclick', onDocumentClick, true);
  };
}
